import cn from 'classnames';
import { FormEvent, forwardRef, useState } from 'react';

import { debounce } from 'src/utils/debounce';

import ArrowForwardIcon from 'assets/icons/arrow_forward.svg';
import CloseXIcon from 'assets/icons/close-x.svg';
import SearchIcon from 'assets/icons/search.svg';

import Text from 'components/BaseHelpers/Text';

import { usePageSearchTypeahead } from '../../usePageSearchTypeahead';
import Typeahead from '../Typeahead';

import styles from './SearchBar.module.scss';

export type SearchBarProps = {
  className?: string;
  onSubmit: () => void;
  onClear?: () => void;
  labels?: {
    termInputPlaceholderLabel: string;
    searchInputLabel: string;
  };
};

const SearchBar = forwardRef<HTMLFormElement, SearchBarProps>(
  function SearchBarComponent(props, ref): JSX.Element {
    const { onSubmit, onClear, labels } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const [showTypeahead, setShowTypeahead] = useState(true);

    const { data: typeaheadData } = usePageSearchTypeahead(searchTerm);

    const handleSubmit = (e?: FormEvent) => {
      e?.preventDefault();
      if (searchTerm === '') {
        return;
      }
      setShowTypeahead(false);
      onSubmit();
    };

    const clearSearch = () => {
      setShowTypeahead(false);
      setSearchTerm('');
      if (onClear) {
        onClear();
      }
    };

    const debouncedTypeahead = debounce((term: string) => {
      setSearchTerm(term);
    }, 100);

    const handleInputChange = (term: string) => {
      if (term.length >= 3) {
        setShowTypeahead(true);
        debouncedTypeahead(term);
      } else {
        debouncedTypeahead('');
      }
    };

    return (
      <form ref={ref} onSubmit={handleSubmit} className={cn(styles.form, props.className)}>
        <label htmlFor="site-search-term" className={styles.termLabel}>
          <Text tag="span" field={labels?.searchInputLabel ?? ''} />
        </label>
        <div className={styles.inputWrapper}>
          <SearchIcon className={styles.searchIcon} />
          <input
            className={styles.input}
            type="search"
            name="term"
            placeholder={labels?.termInputPlaceholderLabel ?? ' '}
            onChange={(e) => handleInputChange(e.target.value)}
            id="site-search-term"
            autoComplete="off"
          ></input>
          <button
            type="button"
            onClick={clearSearch}
            className={styles.clearButton}
            aria-label="Clear term search input"
          >
            <CloseXIcon className={styles.clearIcon} />
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            aria-label="Submit search and navigate to search page."
          >
            <ArrowForwardIcon className={styles.submitIcon} />
          </button>
          {showTypeahead && <Typeahead data={typeaheadData} className={styles.typeahead} />}
        </div>
      </form>
    );
  }
);

export default SearchBar;
